/**
 * Warden — AI Governance practice.
 *
 * These engagements are organized around named instruments. Each page cites the
 * instrument precisely, and none of them claim that Odingard can confer a
 * certification: conformity assessment and certification are the certification
 * body's job, and saying otherwise would be the overclaim that matters here.
 */

import type { ServicePageContent } from "./types";

const PRACTICE = "AI Governance";

const EU_AI_ACT: ServicePageContent = {
  path: "/warden/eu-ai-act",
  practice: PRACTICE,
  title: "EU AI Act Readiness",
  summary:
    "Classify your AI systems, find the high-risk ones, and build the file the Act asks for.",
  headline: "The EU AI Act asks what your systems do, and you have to answer per system",
  lede:
    "A readiness engagement against Regulation (EU) 2024/1689: classify every AI system you operate or place on the market, establish which obligations attach, and build the technical documentation and risk management the Act requires.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=eu-ai-act" },
    { label: "Prove AI Compliance", href: "/ai-compliance" },
  ],
  scope: {
    heading: "Obligations attach to systems, not to companies",
    body:
      "The Act's duties depend on the role you hold for each system — provider, deployer, importer or distributor — and on the risk category that system falls into. Organizations that treat it as one program-level question usually get the classification wrong.",
    points: [
      {
        title: "Role determines duty",
        body: "Fine-tuning and rebranding a model can move you from deployer to provider, with substantially more obligation attached.",
      },
      {
        title: "Classification is per system",
        body: "The same model used for two purposes can be minimal-risk in one deployment and high-risk in another.",
      },
      {
        title: "The technical file is evidence, not narrative",
        body: "Data governance, logging, accuracy and human oversight all have to be demonstrable rather than described.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Inventory and role mapping",
        body: "Every AI system in scope, the role you hold for it, and where it is placed on the market or put into service.",
      },
      {
        title: "Classification",
        body: "Prohibited, high-risk, limited-risk or minimal-risk per system, with the reasoning recorded so it can be defended.",
      },
      {
        title: "Gap analysis",
        body: "For each high-risk system, obligations against current practice: risk management, data governance, logging, transparency, human oversight, accuracy and robustness.",
      },
      {
        title: "Remediation plan",
        body: "Sequenced work with owners, mapped to the applicable dates in the Act's phased application.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A classified AI system inventory with documented reasoning per system",
      "A role determination per system, including where fine-tuning changes it",
      "A per-obligation gap analysis for every high-risk system",
      "Technical documentation structure and the evidence sources that populate it",
      "A remediation plan sequenced against the Act's application dates",
    ],
  },
  drivers: {
    heading: "The instrument itself",
    items: [
      {
        title: "Regulation (EU) 2024/1689",
        body: "The AI Act, applying in phases, with the prohibitions and the general-purpose AI provisions arriving ahead of the full high-risk regime.",
      },
      {
        title: "Extraterritorial reach",
        body: "It applies where the output is used in the Union, not only where the provider is established.",
      },
      {
        title: "Harmonized standards",
        body: "Conformity is expected to be demonstrated largely through harmonized standards as they are published.",
      },
    ],
  },
  products: {
    heading: "What carries the evidence",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
      { label: "ISO 42001 Alignment", href: "/warden/iso-42001" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
    ],
  },
  close: {
    heading: "Classification first, everything else follows",
    body:
      "Most of the cost in an AI Act program is spent on systems that turn out not to be high-risk. Getting the classification right is the saving.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=eu-ai-act" },
      { label: "Govern AI Adoption", href: "/govern-ai" },
    ],
  },
};

const ISO_42001: ServicePageContent = {
  path: "/warden/iso-42001",
  practice: PRACTICE,
  title: "ISO 42001 Alignment",
  summary:
    "Build an AI management system that a certification body will accept.",
  headline: "ISO/IEC 42001 certifies a management system, not a model",
  lede:
    "An alignment engagement that builds the AI management system ISO/IEC 42001:2023 describes — scope, policy, risk and impact assessment, controls, internal audit and management review — and gets you ready for a certification body's audit.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=iso-42001" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "The standard rewards organizations that already run one",
    body:
      "42001 follows the harmonized management-system structure, so if you hold ISO 27001 the shape is familiar and much of the evidence machinery can be reused. What is new is the AI-specific impact assessment and the Annex A controls around data, lifecycle and third parties.",
    points: [
      {
        title: "Scope decides the workload",
        body: "An over-broad scope statement is the most common reason a 42001 program takes twice as long as planned.",
      },
      {
        title: "Impact assessment is not risk assessment",
        body: "The standard asks about consequences for individuals and society, which most existing risk processes do not capture.",
      },
      {
        title: "Certification is a third party's decision",
        body: "We prepare the management system and the evidence; an accredited certification body issues the certificate.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Scope and context",
        body: "Define the boundary of the AI management system, interested parties and objectives.",
      },
      {
        title: "Gap analysis",
        body: "Clauses 4 to 10 and the Annex A controls against what you already operate, reusing ISO 27001 evidence where it applies.",
      },
      {
        title: "Build",
        body: "Policy, roles, AI risk and impact assessment process, Statement of Applicability and the control set.",
      },
      {
        title: "Operate and audit",
        body: "Run the system long enough to produce records, then internal audit and management review ahead of the certification audit.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A defensible scope statement and AI management system policy",
      "AI risk and impact assessment methodology, applied to your real systems",
      "A Statement of Applicability with justification per Annex A control",
      "Internal audit program, findings and management review records",
      "A certification-readiness assessment ahead of the external audit",
    ],
  },
  drivers: {
    heading: "The instrument itself",
    items: [
      {
        title: "ISO/IEC 42001:2023",
        body: "The AI management system standard, certifiable by an accredited body, with Annex A controls specific to AI.",
      },
      {
        title: "ISO/IEC 27001",
        body: "Where you already hold it, the management-system machinery, internal audit and evidence practice carry over.",
      },
      {
        title: "EU AI Act interaction",
        body: "A 42001 management system is useful supporting evidence for AI Act governance obligations, but it is not a substitute for conformity assessment.",
      },
    ],
  },
  products: {
    heading: "What carries the evidence",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "EU AI Act Readiness", href: "/warden/eu-ai-act" },
      { label: "Control Crosswalk Build-out", href: "/warden/crosswalk" },
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
    ],
  },
  close: {
    heading: "A management system that runs, not a binder",
    body:
      "Certification audits fail on missing records far more often than on missing policy. We build for the records.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=iso-42001" },
      { label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
    ],
  },
};

const NIST_AI_RMF: ServicePageContent = {
  path: "/warden/nist-ai-rmf",
  practice: PRACTICE,
  title: "NIST AI RMF Alignment",
  summary:
    "Adopt the Govern, Map, Measure, Manage functions with evidence behind each one.",
  headline: "A voluntary framework still has to be evidenced to be worth citing",
  lede:
    "An alignment engagement against NIST AI RMF 1.0 — establishing the Govern, Map, Measure and Manage functions as practices your teams actually run, with artifacts that support the claim when a customer or regulator asks.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=nist-ai-rmf" },
    { label: "Prove AI Compliance", href: "/ai-compliance" },
  ],
  scope: {
    heading: "The framework is not prescriptive, which is the difficulty",
    body:
      "AI RMF describes outcomes rather than controls. That flexibility is why it is widely adopted and also why self-declared alignment is frequently thin — the framework does not tell you what evidence is sufficient.",
    points: [
      {
        title: "Measure is where programs stall",
        body: "Organizations describe governance comfortably and then have nothing quantitative to show for trustworthiness characteristics.",
      },
      {
        title: "Profiles matter more than the core",
        body: "A use-case profile makes the framework actionable; the core alone produces a generic maturity statement.",
      },
      {
        title: "Nobody certifies it",
        body: "There is no NIST certification. The value is the discipline and the artifacts, so those have to be real.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Current-state profile",
        body: "Assess practice against the four functions and their categories, per AI use case rather than in the abstract.",
      },
      {
        title: "Target profile",
        body: "Set the level of rigor appropriate to your risk tolerance and sector, and record why.",
      },
      {
        title: "Close the Measure gap",
        body: "Define the metrics and test procedures for the trustworthiness characteristics that matter to your use cases.",
      },
      {
        title: "Operationalize Manage",
        body: "Wire monitoring, incident handling and third-party risk into the functions that already run.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "Current-state and target profiles across Govern, Map, Measure and Manage",
      "A use-case profile for each significant AI application",
      "Defined metrics and test procedures for the trustworthiness characteristics in scope",
      "An artifact register showing what evidences each claimed outcome",
      "A crosswalk to ISO/IEC 42001 and the EU AI Act so the work counts once",
    ],
  },
  drivers: {
    heading: "The instrument itself",
    items: [
      {
        title: "NIST AI RMF 1.0",
        body: "Voluntary, outcome-based, organized into Govern, Map, Measure and Manage.",
      },
      {
        title: "Generative AI Profile",
        body: "The companion profile addressing risks specific to generative systems.",
      },
      {
        title: "NIST CSF 2.0",
        body: "Where a cyber program already runs on CSF, the governance function is the natural anchor point.",
      },
    ],
  },
  products: {
    heading: "What carries the evidence",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "ISO 42001 Alignment", href: "/warden/iso-42001" },
      { label: "Model Risk Governance", href: "/warden/model-risk" },
      { label: "Control Crosswalk Build-out", href: "/warden/crosswalk" },
    ],
  },
  close: {
    heading: "Alignment you can substantiate",
    body:
      "Saying you follow AI RMF is easy. Being able to show the profile, the metrics and the records is the part that survives a question.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=nist-ai-rmf" },
      { label: "Govern AI Adoption", href: "/govern-ai" },
    ],
  },
};

const AI_INVENTORY: ServicePageContent = {
  path: "/warden/ai-inventory",
  practice: PRACTICE,
  title: "AI Inventory & Discovery",
  summary: "Find the AI already in use, including everything nobody registered.",
  headline: "Every AI governance program begins with a list nobody has",
  lede:
    "A discovery engagement that finds the models, agents, copilots and embedded AI features actually in use across your organization, and turns them into a maintained inventory rather than a one-off spreadsheet.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=ai-inventory" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "AI arrives through the side door",
    body:
      "Most AI in an enterprise was not procured as AI. It arrived as a feature in a SaaS product already under contract, a developer tool paid for on a card, or a copilot enabled by default in a suite you already own.",
    points: [
      {
        title: "Embedded features are invisible to procurement",
        body: "The vendor added AI to a product you bought three years ago and told you in a release note.",
      },
      {
        title: "Self-declaration undercounts",
        body: "Asking teams what AI they use reliably returns a fraction of what network and expense data show.",
      },
      {
        title: "A static inventory decays immediately",
        body: "Without a maintenance mechanism, the list is wrong within a quarter and worse than useless at audit.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Multi-signal discovery",
        body: "Network egress to model providers, SaaS administration data, expense records, code and configuration, and structured interviews.",
      },
      {
        title: "Classification",
        body: "Purpose, data touched, autonomy level, role held and business criticality per system.",
      },
      {
        title: "Ownership assignment",
        body: "A named accountable owner per entry, because an inventory without owners cannot be maintained.",
      },
      {
        title: "Maintenance mechanism",
        body: "Connectors and an intake process so new AI use registers itself rather than being rediscovered next year.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A classified inventory of AI systems, agents and embedded AI features in use",
      "Data-touched and autonomy classification per entry, ready for regulatory classification",
      "A named owner for every entry",
      "A running discovery and intake mechanism so the inventory stays current",
      "A shadow-AI findings summary for the risk committee",
    ],
  },
  products: {
    heading: "What keeps it current",
    slugs: ["tracelock", "cerberus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "EU AI Act Readiness", href: "/warden/eu-ai-act" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
    ],
  },
  close: {
    heading: "You cannot govern what you have not found",
    body:
      "Discovery is the cheapest engagement here and the one that changes the plan most often.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=ai-inventory" },
      { label: "Govern AI Adoption", href: "/govern-ai" },
    ],
  },
};

const MODEL_RISK: ServicePageContent = {
  path: "/warden/model-risk",
  practice: PRACTICE,
  title: "Model Risk Governance",
  summary:
    "Extend model risk management to systems that do not behave like models.",
  headline: "Your model risk framework was written for models that are deterministic",
  lede:
    "An engagement for regulated institutions extending existing model risk management — SR 11-7 and equivalents — to generative and agentic systems, where validation, monitoring and challenge all need redefining.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=model-risk" },
    { label: "Financial Services", href: "/industry/financial-services" },
  ],
  scope: {
    heading: "Existing MRM assumes a testable, stable function",
    body:
      "Validation practice was built around models with defined inputs, measurable error and stable behavior between releases. A generative system has an open input space, no single correct output and behavior that changes when the provider updates the model underneath you.",
    points: [
      {
        title: "Benchmarking is not validation",
        body: "A score on a public benchmark says little about performance on your population and your task.",
      },
      {
        title: "The vendor changes the model",
        body: "A hosted model can be updated without a change request on your side, which is a version-control problem your framework does not anticipate.",
      },
      {
        title: "Effective challenge needs new skills",
        body: "Independent review cannot challenge what the reviewers cannot evaluate.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Framework gap analysis",
        body: "Where existing MRM policy breaks down when applied to generative and agentic systems.",
      },
      {
        title: "Tiering",
        body: "Risk tiering that reflects autonomy and consequence, not only materiality of the decision.",
      },
      {
        title: "Validation approach",
        body: "Evaluation design, adversarial testing and human-review sampling appropriate to non-deterministic output.",
      },
      {
        title: "Ongoing monitoring",
        body: "Drift, provider version change detection, and the trigger conditions for revalidation.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "Revised model risk policy language covering generative and agentic systems",
      "A tiering methodology that accounts for autonomy and consequence",
      "Validation standards and templates for non-deterministic systems",
      "Ongoing monitoring requirements, including provider model-change detection",
      "A skills and staffing assessment for the independent review function",
    ],
  },
  drivers: {
    heading: "What supervisors expect",
    items: [
      {
        title: "SR 11-7",
        body: "US supervisory guidance on model risk management: development, implementation, use, validation and governance.",
      },
      {
        title: "SS1/23",
        body: "The PRA's model risk management principles for UK banks, with explicit expectations on model identification and tiering.",
      },
      {
        title: "EU AI Act",
        body: "Creditworthiness and certain insurance uses are named high-risk, so the AI Act obligations land on top of MRM.",
      },
    ],
  },
  products: {
    heading: "What carries the evidence",
    slugs: ["tracelock", "argus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "NIST AI RMF Alignment", href: "/warden/nist-ai-rmf" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
    ],
  },
  close: {
    heading: "Extend the framework you have",
    body:
      "Institutions with mature MRM do not need a second framework. They need the existing one to survive contact with a system that improvises.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=model-risk" },
      { label: "Financial Services", href: "/industry/financial-services" },
    ],
  },
};

export const GOVERNANCE_SERVICES: ServicePageContent[] = [
  EU_AI_ACT,
  ISO_42001,
  NIST_AI_RMF,
  AI_INVENTORY,
  MODEL_RISK,
];
